import { memo, type CSSProperties, type SVGProps } from 'react'
import { conditionLabel } from '../../shared/conditions'
import { WX_MARKUP } from './catalog'
import { CONDITION_ICON, wxGlyphId, wxIconCode, wxIconId, type WxCondition, type WxGlyphCode } from './codes'

export type WxTone = 'light' | 'dark' | 'mono'
type SvgAttributes = Omit<SVGProps<SVGSVGElement>,
  'children' | 'dangerouslySetInnerHTML' | 'viewBox' | 'width' | 'height' | 'role' | 'aria-label' | 'aria-hidden'>

export interface WxIconProps extends SvgAttributes {
  condition: WxCondition
  night?: boolean
  /** CSS pixels. The 14–20 px master has fewer details and half-pixel coordinates. */
  size?: number
  /** light artwork for dark skies; dark ink for bright skies; mono uses currentColor. */
  tone?: WxTone
  /** null makes the icon decorative; a string supplies the accessible name. */
  label?: string | null
  /** Opt in only when WxIconSprite is mounted in the same document. */
  sprite?: boolean
}

export interface WxGlyphProps extends Omit<WxIconProps, 'condition' | 'night'> {
  glyph: WxGlyphCode
}

const TONES: Record<WxTone, CSSProperties> = {
  light: { '--wx-ink': '#19374a', '--wx-cloud': '#f8f2e5', '--wx-sun': '#e8bd73', '--wx-ice': '#a7cce0', '--wx-shade': '#9db0bc' } as CSSProperties,
  dark: { '--wx-ink': '#19374a', '--wx-cloud': '#fffdf5', '--wx-sun': '#c28938', '--wx-ice': '#35627a', '--wx-shade': '#3f5e70', '--wx-ice-edge': '#19374a', '--wx-sun-edge': '#19374a' } as CSSProperties,
  mono: { '--wx-ink': 'currentColor', '--wx-cloud': 'none', '--wx-sun': 'currentColor', '--wx-ice': 'currentColor', '--wx-shade': 'currentColor', '--wx-alert': 'none', '--wx-ice-edge': 'currentColor', '--wx-sun-edge': 'currentColor' } as CSSProperties,
}

const GLYPH_LABELS: Partial<Record<WxGlyphCode, string>> = {
  'uv': 'UV index', 'air-quality': 'Air quality', 'advisory': 'Advisory',
  'watch': 'Weather watch', 'warning': 'Weather warning', 'emergency': 'Weather emergency',
  'moon-new': 'New moon', 'moon-waxing-crescent': 'Waxing crescent',
  'moon-first-quarter': 'First quarter', 'moon-waxing-gibbous': 'Waxing gibbous',
  'moon-full': 'Full moon', 'moon-waning-gibbous': 'Waning gibbous',
  'moon-last-quarter': 'Last quarter', 'moon-waning-crescent': 'Waning crescent',
}

function name(condition: WxCondition): string {
  if (Object.hasOwn(CONDITION_ICON, condition)) return conditionLabel(condition as keyof typeof CONDITION_ICON)
  const code = wxIconCode(condition)
  return code[0].toUpperCase() + code.slice(1).replaceAll('-', ' ')
}

const Icon = memo(function Icon({ symbolId, size = 24, tone = 'light', label, sprite, style, ...rest }: Omit<WxIconProps, 'condition' | 'night'> & { symbolId: string }) {
  const pixels = Number.isFinite(size) && size > 0 ? size : 24
  const compact = pixels <= 20
  const key = symbolId.replace('wx-v5-', '') + (compact ? '-compact' : '')
  const dimensions = { width: pixels, height: pixels }
  return <svg
    {...rest}
    {...dimensions}
    viewBox={`0 0 ${compact ? 16 : 24} ${compact ? 16 : 24}`}
    fill="none"
    strokeWidth={compact ? 1.25 : 1.4}
    strokeLinecap="round"
    strokeLinejoin="round"
    role={label == null ? undefined : 'img'}
    aria-label={label ?? undefined}
    aria-hidden={label == null ? true : undefined}
    focusable="false"
    data-wx-icon={key}
    data-wx-tone={tone}
    style={{ ...TONES[tone], ...style, display: 'inline-block', verticalAlign: 'middle', flexShrink: 0, ...dimensions }}
    {...(!sprite ? { dangerouslySetInnerHTML: { __html: WX_MARKUP[key] } } : {})}
  >{sprite ? <use href={`#wx-v5-${key}`} /> : undefined}</svg>
})

/** First-paint-safe: inline contours by default, no effects, requests, fonts, or registration. */
export function WxIcon({ condition, night = false, label, ...props }: WxIconProps) {
  return <Icon {...props} symbolId={wxIconId(condition, night)} label={label === undefined ? name(condition) : label} />
}

export function WxGlyph({ glyph, label, tone = 'mono', style, ...props }: WxGlyphProps) {
  const defaultName = GLYPH_LABELS[glyph] ?? glyph[0].toUpperCase() + glyph.slice(1).replaceAll('-', ' ')
  const lightInk = tone === 'light' ? { '--wx-ink': '#f8f2e5', '--wx-cloud': 'none', '--wx-alert': 'none' } as CSSProperties : undefined
  return <Icon {...props} style={lightInk ? { ...lightInk, ...style } : style} tone={tone} symbolId={wxGlyphId(glyph)} label={label === undefined ? defaultName : label} />
}
